import { Outlet, createBrowserRouter, useRouteError } from 'react-router-dom';
import { BreadcrumbsProvider, useUser } from './Hooks';
import SiteContainer from './SiteContainer';
import { Prospects, LoginResponse, Promoters, Promoter, PromoterEvents, PromoterConditions, PromoterAssets, PromoterFormView, PromoterSurveys, SurveyResponses, ProspectFormView, PublicFormPage, PublicSurveyPage, Forms, FormMetadata, Surveys, SurveyQuestion, ActivityTypesList, ProspectRegister, Users, UserView, Inactive, ActivitiesMaster, DateReport, PromotersReport, SurveyReport, ExternalEvents, StatesReport, ProspectsMode } from './screens';
import { SiteRoute } from './SiteClasses';
import EntitySidebar from './sidebars/EntitySidebar';

export const ROUTES: SiteRoute[] = [
	{ path: '/', title: 'Prospectos', iconName: 'user-plus' },
	{
		path: 'prospects',
		title: 'Prospectos',
		iconName: 'user-plus',
		ignore: true,
		children: [
			{ path: 'register', title: 'Registrar prospecto', iconName: 'user-plus' },
			{
				path: ':prospect_id',
				title: p=>`Prospecto #${p.prospect_id}`,
				ignore: true,
				sidebar: <EntitySidebar idParam='prospect_id' basePath='prospects' label='Prospecto' />,
				children: [
					{ path: 'form', title: 'Formulario', iconName: 'file-alt' },
					{ path: 'evidences', title: 'Evidencias', iconName: 'file' },
				],
			},
		],
	},
	{
		path: 'forms',
		title: 'Formularios',
		iconName: 'file-alt',
		children: [
			{ path: 'create', title: 'Crear pregunta' },
			{ path: ':qid', title: p=>`Pregunta #${p.qid}` },
		],
	},
	{
		path: 'surveys',
		title: 'Encuesta',
		iconName: 'clipboard-question',
		children: [
			{ path: 'create', title: 'Crear pregunta' },
			{ path: ':qid', title: p=>`Pregunta #${p.qid}` },
		],
	},
	{ path: 'responses', title: 'Encuestas', iconName: 'clipboard-check' },
	{ path: 'activities', title: 'Actividades', iconName: 'tasks' },
	{ path: 'externals', title: 'Eventos externos', iconName: 'file-excel' },
	{ path: 'reports/dates', title: 'Reporte de fechas', iconName: 'calendar' },
	{ path: 'reports/promoters', title: 'Reporte promotores', iconName: 'user-tie' },
	{ path: 'reports/surveys', title: 'Reporte de encuestas', iconName: 'chart-pie' },
	{ path: 'reports/states', title: 'Mapa de eventos', iconName: 'map' },
	{ path: 'types', title: 'Tipos de actividad', iconName: 'list' },
	{
		path: 'users',
		title: 'Usuarios',
		iconName: 'user',
		children: [
			{ path: ':user_id', title: p=>`Usuario #${p.user_id}` },
		],
	},
	{
		path: 'promoters',
		title: 'Promotores',
		iconName: 'user-tie',
		children: [
			{
				path: ':promoter_id',
				title: p=>`Promotor #${p.promoter_id}`,
				ignore: true,
				sidebar: <EntitySidebar idParam='promoter_id' basePath='promoters' label='Promotor' />,
				children: [
					{ path: 'form', title: 'Formulario', iconName: 'file-alt' },
					{ path: 'events', title: 'Eventos', iconName: 'calendar' },
					{ path: 'conditions', title: 'Condiciones', iconName: 'clipboard-list' },
					{ path: 'surveys', title: 'Encuestas', iconName: 'clipboard-question' },
					{ path: 'evidences', title: 'Evidencias', iconName: 'file' },
				],
			},
		],
	},
];

const RouterComponent = ()=>{
	var { hasAccess } = useUser();

	var ErrorElement = ()=>{
		const error = (useRouteError() as any);
		return <div>
			<div className="fr centered header" style={{ fontSize: 100 }}>
				{error.status}
				<div className="sub header" style={{ fontSize: 20 }}>
					{error.status===404 ? 'Página no encontrada' : (
						hasAccess(6000) ? error.message : 'Hubo un error inesperado.'
					)}
				</div>
			</div>
		</div>
	}

	const Router = createBrowserRouter([
		{
			path: '/auth',
			element: <LoginResponse />,
		},
		{
			path: '/apply/:promoter_id',
			element: <PublicFormPage />,
		},
		{
			path: '/survey/:token',
			element: <PublicSurveyPage />,
		},
		{
			path: '/register',
			element: <ProspectRegister mode='public' />,
		},
		{
			path: '/',
			errorElement: <BreadcrumbsProvider routes={ROUTES}><SiteContainer outlet={<ErrorElement />} /></BreadcrumbsProvider>,
			element: <BreadcrumbsProvider routes={ROUTES}><SiteContainer outlet={<Outlet />} /></BreadcrumbsProvider>,
			children: [
				{ path: '/', element: <Prospects /> },
				{ path: 'prospects/register', element: <ProspectRegister mode='admin' /> },
				{ path: 'prospects/:prospect_id', element: <Promoter /> },
				{ path: 'prospects/:prospect_id/form', element: <ProspectFormView /> },
				{ path: 'prospects/:prospect_id/evidences', element: <PromoterAssets /> },
				{ path: 'promoters', element: <Promoters /> },
				{ path: 'inactive', element: <Inactive /> },
				{ path: 'convert', element: <Prospects mode={ProspectsMode.CONVERTIR} /> },
				{ path: 'postponed', element: <Prospects mode={ProspectsMode.APLAZADO} /> },
				{ path: 'disqualified', element: <Prospects mode={ProspectsMode.DESCALIFICADO} /> },
				{ path: 'forms', element: <Forms /> },
				{ path: 'forms/:qid', element: <FormMetadata /> },
				{ path: 'surveys', element: <Surveys /> },
				{ path: 'surveys/:qid', element: <SurveyQuestion /> },
				{ path: 'responses', element: <SurveyResponses /> },
				{ path: 'activities', element: <ActivitiesMaster /> },
				{ path: 'externals', element: <ExternalEvents /> },
				{ path: 'reports/dates', element: <DateReport /> },
				{ path: 'reports/promoters', element: <PromotersReport /> },
				{ path: 'reports/surveys', element: <SurveyReport /> },
				{ path: 'reports/states', element: <StatesReport /> },
				{ path: 'types', element: <ActivityTypesList /> },
				{ path: 'users', element: <Users /> },
				{ path: 'users/:user_id', element: <UserView /> },
				{ path: 'promoters/:promoter_id', element: <Promoter /> },
				{ path: 'promoters/:promoter_id/form', element: <PromoterFormView /> },
				{ path: 'promoters/:promoter_id/events', element: <PromoterEvents /> },
				{ path: 'promoters/:promoter_id/conditions', element: <PromoterConditions /> },
				{ path: 'promoters/:promoter_id/surveys', element: <PromoterSurveys /> },
			{ path: 'promoters/:promoter_id/evidences', element: <PromoterAssets /> },
			]
		}
	]);

	return Router;
}

export default RouterComponent;
